/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
    Button, Dropdown,
    Grid,
    Input, Pagination,
    Segment,
} from 'semantic-ui-react'
import { archive, exportSearchItems, getMerchantItems,countMerchantItems } from '../../../services/Item/Item.service'
import { debounce } from 'lodash'
import { useTranslation } from 'react-i18next'
import useUser from '../../Shared/UserProvider/useUser'
import { useNavigate } from 'react-router-dom-v5-compat'
import useMediaQuery from 'beautiful-react-hooks/useMediaQuery'
import { saveFile } from '../../../services/File/File.service'
import useBreadcrumb from '../../NavBar/useBreadcrumb'
import AdvancedFilters from './AdvancedFilters'
import { UltyModalWrapperContext } from '../../Shared/UltyModalWrapper/UltyModalWrapperContext'
import {
    filtersToQueryString,
    formatFilterValueFromType,
    useQuerySearchParams,
} from '../../../services/Filter/filter.service'
import UltyFilterBar from '../../Shared/UltyFilterBar/UltyFilterBar'
import MassActionBar from '../../Shared/MassActionBar/MassActionBar'
import MassActionConfirm from './MassActions/MassActionConfirm'
import { patchItemAvailability } from '../../../services/Provider/Provider.service'
import { getCategorySets } from '../../../services/Category/Category.service'
import { Sorting, SortingTypes } from './Sorting/Sorting'
import { ItemListSegment } from './ItemListSegment'
import { useScanning } from '../../../scanning/scanning.service'
import { ultyToast } from '../../Shared/UltyToast/UltyToast'
import { useInvalidateCountAndSearch } from '../useInvalidateCountAndSearch'

const itemListMassActions = {
    EDIT: 'edit',
    SET_AVAILABLE: 'setAvailable',
    SET_UNAVAILABLE: 'setUnavailable',
    SET_UNAVAILABLE_UNTIL_TONIGHT: 'setUnavailableUntilTonight',
    DELETE: 'delete',
}

const getSortingFromString = (str) => {
    if (!str) return undefined
    const splitted = str.toLowerCase().split('|')

    if (splitted.length !== 2) return undefined

    const [type, direction] = splitted
    if (!SortingTypes.includes(type)) return undefined

    return {
        type,
        direction: direction === 'desc' ? 'desc' : 'asc',
    }
}

const getSortingToString = (sorting) => {
    return sorting ? `${sorting.type}|${sorting.direction}` : undefined
}

const ItemsList = () => {
    const {user} = useUser()
    const {setPaths} = useBreadcrumb()
    const [t] = useTranslation()
    const {handleUltyModalWrapper} = useContext(UltyModalWrapperContext)

    const [selectedItems, setSelectedItems] = useState([])
    const [categories, setCategories] = useState([])

    const navigate = useNavigate();
    const { invalidateCountAndSearch } = useInvalidateCountAndSearch()

    const query = useQuerySearchParams()
    const queryFilterTypes = query.get('types')
    const queryFilterContainsAlcohol = JSON.parse(query.get('containsAlcohol'))
    const queryFilterImage = JSON.parse(query.get('withImage'))
    const queryFilterUnavailable = JSON.parse(query.get('unavailableUntil'))
    const queryFilterIsUncountable = JSON.parse(query.get('isUncountable'))
    const queryFilterMaxQuantity = JSON.parse(query.get('maxQuantity'))
    const queryFilterCategorySet = query.get('categorySet')
    const queryFilterCategorySlot = query.get('categorySlot')
    const queryFilterCategory = query.get('category')
    const queryFilterCategoryExclusion = query.get('excludeCategories')
    const queryFilterSearch = query.get('search')
    const queryFilterMeasurementType = query.get('measurementUnitType')
    const queryFilterProviderType = query.get('providerType')
    const querySorting = query.get('sort')

    const queryPage = query.get("page");
    const queryPageSize = query.get("size");

    const [pagination, setPagination] = useState({
        totalItems: 0,
        totalPages: 1,
        page: Number(query.get('page')) || 1,
        pageSize: Number(query.get('size')) || 30,
    });

    const filters = useMemo(() => ({
        types: queryFilterTypes?.split(',') || undefined,
        containsAlcohol: queryFilterContainsAlcohol !== null ? queryFilterContainsAlcohol : undefined,
        withImage: queryFilterImage !== null ? queryFilterImage : undefined,
        unavailableUntil: queryFilterUnavailable !== null ? queryFilterUnavailable : undefined,
        isUncountable: queryFilterIsUncountable !== null ? queryFilterIsUncountable : undefined,
        maxQuantity: queryFilterMaxQuantity !== null ? queryFilterMaxQuantity : undefined,
        categorySet: queryFilterCategorySet !== null ? queryFilterCategorySet : undefined,
        categorySlot: queryFilterCategorySlot !== null ? queryFilterCategorySlot : undefined,
        category: queryFilterCategory !== null ? queryFilterCategory : undefined,
        excludeCategories: queryFilterCategoryExclusion !== null ? !!queryFilterCategoryExclusion : undefined,
        search: queryFilterSearch !== null ? queryFilterSearch : undefined,
        measurementUnitType: queryFilterMeasurementType?.split(',') || undefined,
        providerType: queryFilterProviderType !== null ? queryFilterProviderType : undefined,
    }), [queryFilterCategory, queryFilterCategoryExclusion, queryFilterCategorySet, queryFilterCategorySlot, queryFilterContainsAlcohol, queryFilterImage, queryFilterIsUncountable, queryFilterMaxQuantity, queryFilterMeasurementType, queryFilterProviderType, queryFilterSearch, queryFilterTypes, queryFilterUnavailable])

    const sorting = useMemo(() => {
        return getSortingFromString(querySorting)
    },[querySorting])

    const [searchField, setSearchField] = useState(filters.search)
    const [searchExportInProgress, setSearchExportInProgress] = useState(false)

    const {data:items,isFetching:itemsAreLoading,isFetched} = useQuery({
        queryKey: ['items','search', filters, pagination.page, pagination.pageSize, getSortingToString(sorting)],
        refetchOnWindowFocus:false,
        placeholderData: keepPreviousData,
        queryFn: async ({ signal }) => {
            const result  = await getMerchantItems({
                   ...filters,
                page: pagination.page,
                size: pagination.pageSize,
                sort: getSortingToString(sorting),
                noCount:true
            }, signal);
            return result?.items ?? []
        }
    });

    const {data:count,isFetching:countIsLoading} = useQuery({
        queryKey: ['items','search-count', filters],
        refetchOnWindowFocus:false,
        placeholderData: keepPreviousData,
        queryFn: ({ signal }) => countMerchantItems({
            ...filters,
        }, signal),
    });

    const canScan = useMemo(() => isFetched, [isFetched]);


    useScanning((barcodeEvent) => {
        handleSearchChange(null, barcodeEvent)
    }, canScan);


    const isMobile = useMediaQuery(`(max-width: 768px)`)

    useEffect(() => {
        setPaths([{
            text: t('breadcrumb.home'),
            link: true,
            path: '/',
        }, {
            text: t('breadcrumb.catalog'),
            link: false,
            path: '/products',
        }])
    }, [setPaths, t])

    const canStillSelectItems = useMemo(() => {
        return items && items.some(t => !selectedItems.map(t => t.id).includes(t.id))
    }, [selectedItems, items])

    useEffect(() => {
        (async function loadCategories() {
            try {
                const categories = await getCategorySets()
                setCategories(categories.map(set => ({
                    label: set.name,
                    value: set.id,
                    slots: set.slots.map(slot => ({
                        label: slot.name,
                        value: slot.id,
                        categories: slot.categories.map(category => ({
                            label: category.name,
                            value: category.id,
                        })),
                    })),
                })))
            } catch (e) {
                console.log(e)
                ultyToast({ type: 'error', title: t('global.anErrorOccurred') })
            }
        })()
    }, [t])

    useEffect(() => {
        setPagination(p => ({
            ...p,
            page:Number(queryPage) || 1,
            pageSize:Number(queryPageSize) || 30,
        }));
    },[queryPage,queryPageSize])

    useEffect(() => {
        if(!itemsAreLoading && !countIsLoading && items){
            const totalPages = Math.ceil(count / +(pagination.pageSize)) || 1;
            setPagination(p => ({
                ...p,
                totalItems: count,
                totalPages
            }))
        }
    },[itemsAreLoading,countIsLoading,items,count,pagination.pageSize])

    const exportItems = useCallback(async () => {
        setSearchExportInProgress(true)
        try {
            const {data} = await exportSearchItems({
                ...filters,
                page: pagination.page,
                size: pagination.pageSize,
                sort: getSortingToString(sorting),
            })
            saveFile(t('category_set.export_search_file_name'), data, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
        } finally {
            setSearchExportInProgress(false)
        }

    }, [filters,pagination.page,pagination.pageSize,sorting])

    const handleSelectedItems = useCallback((item) => {
        setSelectedItems(prev => (
          prev.map(item => item.id).includes(item.id) ?
            prev.filter(selectedItem => selectedItem.id !== item.id) :
            [...prev, item]))
    }, [])

    const replaceHistory = useCallback((filters, pagination, sorting = undefined) => {
        navigate({
            pathname: '/products',
            replace: true,
            search: filtersToQueryString({
                ...filters,
                page: pagination.page,
                size: pagination.pageSize,
                sort: getSortingToString(sorting),
            }),
        })
    }, [navigate])

    const handleFiltersChange = useCallback((filters) => {
        replaceHistory(filters, {...pagination,page:1}, sorting)
        handleUltyModalWrapper(false)
    }, [replaceHistory, pagination, sorting, handleUltyModalWrapper])

    const onSearch = useCallback(async (value, currentFilters) => {
        if (value) {
            handleFiltersChange({...currentFilters, search: value})
        } else {
            const {search, ...filtersWithoutSearch} = currentFilters
            handleFiltersChange(filtersWithoutSearch)
        }
    }, [handleFiltersChange])

    const debouncedSearch = useMemo(() => (
      debounce((value, currentFilters) => onSearch(value, currentFilters), 500)
    ), [onSearch])


    const handleSearchChange = async (e, {value}) => {
        setSearchField(value)
        debouncedSearch(value, filters)
    }

    const handleOnPageChange = (e, paginationProps) => {
        setPagination({
            ...pagination,
            page: paginationProps.activePage,
        })

        replaceHistory(filters, {...pagination, page: paginationProps.activePage}, sorting)
    }

    const onPageSizeChange = (e, paginationProps) => {
        setPagination({
            ...pagination,
            page: 1,
            pageSize: paginationProps.value,
        })

        replaceHistory(filters, {page: 1, pageSize: paginationProps.value}, sorting)
    }

    const onSortingChange = useMemo(() => debounce((sorting, currentFilters, currentPagination) => {
        replaceHistory(currentFilters, {...currentPagination, page: 1}, sorting);
    }, 500), [replaceHistory]);


    const handleOpenAdvancedFiltersModal = () => {
        const modalSettings = {
            size:'huge',
            component: <AdvancedFilters
              defaultFilters={filters}
              onFiltersChange={handleFiltersChange}
            />,
        }

        handleUltyModalWrapper(true, modalSettings)
    }

    const formatFilterData = (type, value) => {
        let formattedLabel
        let formattedValue

        switch (type) {
            case 'types':
                formattedValue = formatFilterValueFromType('array', type, value, 'global.enums.itemTypes')
                formattedLabel = `${t('product_list.advanced_filters.general.item_types')} : ${formattedValue}`
                break
            case 'containsAlcohol':
                formattedValue = formatFilterValueFromType('boolean', type, {
                    raw: value,
                    true: t('product_list.advanced_filters.general.alcohol.with'),
                    false: t('product_list.advanced_filters.general.alcohol.without'),
                })
                formattedLabel = `${t('product_list.advanced_filters.general.alcohol.alcohol')} : ${formattedValue}`
                break
            case 'withImage':
                formattedValue = formatFilterValueFromType('boolean', type, {
                    raw: value,
                    true: t('product_list.advanced_filters.general.image.with'),
                    false: t('product_list.advanced_filters.general.image.without'),
                })
                formattedLabel = `${t('product_list.advanced_filters.general.image.image')} : ${formattedValue}`
                break
            case 'unavailableUntil':
                formattedValue = formatFilterValueFromType('boolean', type, {
                    raw: value,
                    true: t('product_list.advanced_filters.inventory.availability.unavailable'),
                    false: t('product_list.advanced_filters.inventory.availability.available'),
                })
                formattedLabel = `${t('product_list.advanced_filters.inventory.availability.availability')} : ${formattedValue}`
                break
            case 'maxQuantity':
                formattedValue = formatFilterValueFromType('primitive', type, value)
                formattedLabel = `${t('product_list.advanced_filters.inventory.quantity.available_quantity')} ${formattedValue}`
                break
            case 'isUncountable':
                formattedValue = formatFilterValueFromType('boolean', type, {
                    raw: value,
                    true: t('product_list.advanced_filters.inventory.uncountable.with'),
                    false: t('product_list.advanced_filters.inventory.uncountable.without'),
                })
                formattedLabel = `${t('product_list.advanced_filters.inventory.uncountable.uncountable')} : ${formattedValue}`
                break
            case 'categorySet': {
                const [cs] = categories.filter(c => c.value === value)
                if (cs) {
                    formattedValue = cs.label
                    formattedLabel = `${t('product_list.advanced_filters.menu.menu')} : ${formattedValue}`
                }
                break
            }
            case 'categorySlot': {
                const [cs] = categories.flatMap(c => c.slots).filter(c => c.value === value)
                if (cs) {
                    formattedValue = cs.label
                    formattedLabel = `${t('product_list.advanced_filters.menu.submenu')} : ${formattedValue}`
                }
                break
            }
            case 'category': {
                const [cs] = categories.flatMap(c => c.slots).flatMap(c => c.categories).filter(c => c.value === value)
                if (cs) {
                    formattedValue = cs.label
                    formattedLabel = `${t('product_list.advanced_filters.menu.category')} : ${formattedValue}`
                }
                break
            }
            case 'excludeCategories': {
                formattedLabel = `${t('product_list.advanced_filters.menu.without_category')}`
                break
            }
            case 'search': {
                formattedValue = formatFilterValueFromType('primitive', type, value)
                formattedLabel = `${t('product_list.advanced_filters.search')} : "${formattedValue}"`
                break
            }
            case 'measurementUnitType': {
                formattedValue = formatFilterValueFromType('array', type, value, 'global.enums.measurementTypes')//value.map(v => t(`global.enums.measurementTypes.${v}`)).join(', ')
                formattedLabel = `${t('product_list.advanced_filters.general.measurement_type')} : ${formattedValue}`
                break
            }
            case 'providerType': {
                formattedValue = t(`provider.type_${value}`)
                formattedLabel = `${t('product_list.advanced_filters.general.provider_type')} : ${formattedValue}`
                break
            }
            default:
                formattedLabel = ''
        }

        return formattedLabel
    }

    const handleClearSelection = () => {
        setSelectedItems([])
        handleUltyModalWrapper(false, null)
    }

    const handleSelectAll = () => {
        setSelectedItems(items)
    }

    const handleViewSelection = () => {
        const modalSettings = {
            title: t('mass_action_bar.current_selection'),
            component: <MassActionConfirm
              selectedItems={selectedItems}
              onItemsChange={setSelectedItems}
              onMassAction={handleMassActionByKey}
              afterSubmit={() => handleUltyModalWrapper(false, null)}
            />,
        }

        handleUltyModalWrapper(true, modalSettings)
    }

    const performMassAction = async (promises, successMessage) => {
        try {
            await promiseAllInBatches(promises, 5)
            setSelectedItems([])
            const {promises:invalidations} = invalidateCountAndSearch();
            await invalidations;

            ultyToast({ type: 'success', title: successMessage })
        } catch (e) {
            console.log(e)
            ultyToast({ type: 'error', title: t('global.anErrorOccurred') })
        }
    }

    const promiseAllInBatches = async (fcts, batchSize) => {
        let position = 0
        let results = []
        while (position < fcts.length) {
            const fctsForBatch = fcts.slice(position, position + batchSize)
            results = [...results, ...await Promise.all(fctsForBatch.map(fct => fct()))]
            position += batchSize
        }
        return results
    }

    const handleMassActionSetAvailable = async (selectedItems) => {
        const promises = selectedItems.map(item => () => patchItemAvailability(user.provider.id, item.id, null))
        await performMassAction(promises, t('product_list.mass_actions.set_available.set_available_success'))
    }

    const handleMassActionSetUnavailable = async (selectedItems) => {
        const promises = selectedItems.map(item => () => patchItemAvailability(user.provider.id, item.id, 'infinity'))
        await performMassAction(promises, t('product_list.mass_actions.set_unavailable.set_unavailable_success'))
    }

    const handleMassActionSetUnavailableUntilTonight = async (selectedItems) => {
        const tomorrow = new Date()
        tomorrow.setUTCDate(tomorrow.getUTCDate() + 1)
        tomorrow.setUTCHours(0, 0, 0, 0)

        const promises = selectedItems.map(item => () => patchItemAvailability(user.provider.id, item.id, tomorrow))
        await performMassAction(promises, t('product_list.mass_actions.set_unavailable.set_unavailable_success'))
    }

    const handleMassActionDelete = async (items) => {
        const promises = items.map(item => () => archive(item.id, {archived: true}))
        await performMassAction(promises, t('product_list.mass_actions.delete.delete_success'))
    }

    const handleMassActionByKey = async (massActionKey, selectedItems) => {
        switch (massActionKey) {
            case itemListMassActions.SET_AVAILABLE:
                await handleMassActionSetAvailable(selectedItems)
                break
            case itemListMassActions.SET_UNAVAILABLE:
                await handleMassActionSetUnavailable(selectedItems)
                break
            case itemListMassActions.SET_UNAVAILABLE_UNTIL_TONIGHT:
                await handleMassActionSetUnavailableUntilTonight(selectedItems)
                break
            case itemListMassActions.DELETE:
                await handleMassActionDelete(selectedItems)
                break
            default:
                break
        }
    }

    const ItemPaging = () => {
        return <Segment basic textAlign="center">
            {!countIsLoading && (
            <Pagination
                firstItem={null}
                lastItem={null}
                pointing
                secondary
                siblingRange={isMobile ? 0 : 1}
                activePage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={handleOnPageChange}
            />
            )}
        </Segment>
    }

    return (
      <>
          <Segment css={css`
              position: sticky !important;
              top: 65px;
              z-index: 10;
          `}>
              <Grid>
                  <Grid.Row css={css` padding-bottom: 0 !important; `}>
                      <Grid.Column width={6}>
                          <div css={css`
                              display: flex;
                              flex-wrap: wrap;
                              row-gap: 1rem;
                              justify-content: space-between;
                          `}>
                              <MassActionBar
                                selectionCount={selectedItems.length}
                                onViewSelection={handleViewSelection}
                                onClearSelection={handleClearSelection}
                                onSelectAll={handleSelectAll}
                                canStillSelectItems={canStillSelectItems}
                              />
                          </div>
                      </Grid.Column>
                      <Grid.Column width={10} textAlign="right">
                          <div css={css`
                              display: flex;
                              flex-grow: ${isMobile ? 1 : 0};
                              column-gap: .5rem;
                              justify-content: flex-end;
                          `}>
                              <Input
                                fluid={isMobile}
                                css={css`
                                    flex-grow: ${isMobile ? 1 : 0};
                                `}
                                icon="search"
                                placeholder={t('product_list.filters.filter_placeholder')}
                                value={searchField}
                                onChange={handleSearchChange}
                              />
                              <Button
                                icon="filter"
                                color={'teal'}
                                title={t('product_list.advanced_filters.advanced_filters')}
                                onClick={handleOpenAdvancedFiltersModal}
                              />
                          </div>
                      </Grid.Column>
                  </Grid.Row>
                  <Grid.Row columns={2} css={css` padding-bottom: 0 !important; `}>
                      <Grid.Column verticalAlign="middle" textAlign="left">
                          {t('product_list.filters.items_per_page')} : &nbsp;&nbsp;
                          <Dropdown options={[
                              {key: '10', value: 10, text: '10'},
                              {key: '30', value: 30, text: '30'},
                              {key: '50', value: 50, text: '50'},
                              {key: '100', value: 100, text: '100'},
                          ]} value={pagination.pageSize} onChange={onPageSizeChange}/>

                      </Grid.Column>

                      <Grid.Column floated="left" css={css`
                          font-style: italic;
                          display: flex;
                          flex-direction: row;
                          align-items: center;
                          justify-content: flex-end;
                      `} textAlign="right">
                      </Grid.Column>
                  </Grid.Row>
                  <Grid.Row columns={2}>
                      <Grid.Column verticalAlign="middle" textAlign="left">
                          <Sorting onSortChange={(sorting) => onSortingChange(sorting, filters, pagination)} defaultSort={sorting}></Sorting>
                      </Grid.Column>

                      <Grid.Column floated="left" css={css`
                          font-style: italic;
                          display: flex;
                          flex-direction: row;
                          align-items: center;
                          justify-content: flex-end;
                      `} textAlign="right">
                            {!countIsLoading && (<span css={css`
                                margin-right: 0.6em
                            `}>
                                {t('product_list.num_items', {n: `${pagination.totalItems}`})}

                            </span>)}

                          {/* Disable export items for this POS because Videor competitor is trying to convince POS employees to send them the catalog */}
                          {((user.isStaff && user?.provider?.id === 'c7e5200c-94e2-40d0-bbf0-5389c2cab8c5') || user?.provider?.id !== 'c7e5200c-94e2-40d0-bbf0-5389c2cab8c5') ? (
                            <Button
                              as="a"
                              title="Download"
                              loading={searchExportInProgress}
                              basic compact icon="download"
                              onClick={exportItems}
                              disabled={searchExportInProgress}>
                            </Button>
                          ) : null}

                      </Grid.Column>
                  </Grid.Row>
              </Grid>
          </Segment>

          <UltyFilterBar
            css={css`margin-bottom: 1rem;`}
            filterAttributes={filters}
            formatFilterDataFn={formatFilterData}
            onFilterRemoval={(k) => {
                const newFilters = {...filters}
                newFilters[k] = undefined
                handleFiltersChange(newFilters)
            }}
          />

          <ItemListSegment
            isLoading={itemsAreLoading}
            items={items}
            onSelectItem={handleSelectedItems}
            selectedItems={selectedItems}
          />

          <ItemPaging/>
      </>
    )
}

export default ItemsList
